// Survives the round trip to an off-site payment step (3-D Secure, wallets).
//
// Stripe returns the customer to `return_url`, which reloads the SPA and wipes
// every bit of React state the booking was built from. Without this the
// customer lands on an empty step 1 seconds after their card was charged — and
// the obvious reaction to that is to book and pay a second time. The snapshot
// is written before the charge is attempted so the confirmation can be rebuilt
// on the way back.

export interface ConfirmationRow {
	label: string;
	value: string;
	variant?: 'positive';
}

export interface ConfirmationSnapshot {
	/** Needed to ask the server whether the appointment has been created yet. */
	paymentIntentId: string;
	clientSecret: string;
	publishableKey: string;
	rows: ConfirmationRow[];
}

// sessionStorage, not localStorage: this is one booking attempt in one tab, and
// it should not outlive the tab or leak into an unrelated later visit.
const storageKey = (token: string) => `bbp_public_booking_${token}`;

export const saveConfirmationSnapshot = (token: string, snapshot: ConfirmationSnapshot): void => {
	try {
		sessionStorage.setItem(storageKey(token), JSON.stringify(snapshot));
	} catch {
		// Private browsing or a full quota — the in-memory flow still works, only
		// the redirect recovery is lost. Never break the booking over this.
	}
};

export const readConfirmationSnapshot = (token: string): ConfirmationSnapshot | null => {
	try {
		const raw = sessionStorage.getItem(storageKey(token));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as ConfirmationSnapshot;
		return parsed && Array.isArray(parsed.rows) ? parsed : null;
	} catch {
		return null;
	}
};

export const clearConfirmationSnapshot = (token: string): void => {
	try {
		sessionStorage.removeItem(storageKey(token));
	} catch {
		// Nothing to do — a stale snapshot is only read after a Stripe redirect.
	}
};

/**
 * The secret Stripe appends to `return_url` when it sends the customer back.
 * Its presence is what marks this load as a return leg rather than a fresh
 * visit.
 */
export const getReturnedClientSecret = (search: string): string | null =>
	new URLSearchParams(search).get('payment_intent_client_secret');

/**
 * The PaymentIntent id Stripe appends alongside the secret. Lets the return
 * leg poll the booking status even when the snapshot could not be stored.
 */
export const getReturnedPaymentIntentId = (search: string): string | null =>
	new URLSearchParams(search).get('payment_intent');

/**
 * Drops Stripe's redirect params so a refresh does not look like a return leg
 * forever. Keeps the booking token, which is what the page is addressed by.
 */
export const stripPaymentParamsFromUrl = (): void => {
	const url = new URL(window.location.href);
	['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach(param =>
		url.searchParams.delete(param)
	);
	window.history.replaceState({}, '', url.toString());
};
